import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
    Button, Flex, Form, Input, Modal, Select, Spin,
} from 'antd';
import {
    useCallback, useEffect, useMemo,
} from 'react';
import {
    Controller, useForm,
} from 'react-hook-form';
import { z } from 'zod';

import { supportTicketsMutations } from '@/api';
import { SupportTicketStatus } from '@/constants';
import {
    SupportTicket, SupportTicketUpdatePayload, 
} from '@/types';

import { StatusTag } from './StatusTag';

const supportTicketStatusValues = Object.values(SupportTicketStatus) as [SupportTicketStatus, ...SupportTicketStatus[]];

const supportTicketEditSchema = z.object({
    status: z.enum(supportTicketStatusValues),
    notes: z.string().max(255, 'Максимум 255 символів').optional().nullable(),
});

type SupportTicketEditFormData = z.infer<typeof supportTicketEditSchema>;

type SupportTicketEditModalProps = {
    open: boolean;
    onClose: () => void;
    ticket: SupportTicket | null;
};

const statusOptions = Object.values(SupportTicketStatus).map((value) => ({
    value,
    label: <StatusTag status={value} />,
}));

export const SupportTicketEditModal = ({ open, onClose, ticket }: SupportTicketEditModalProps) => {
    const defaultValues = useMemo<SupportTicketEditFormData>(() => ({
        status: ticket?.status as SupportTicketStatus || SupportTicketStatus.New,
        notes: ticket?.notes || null,
    }), [ticket]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<SupportTicketEditFormData>({
        resolver: zodResolver(supportTicketEditSchema),
        defaultValues,
        mode: 'onChange',
    });

    useEffect(() => {
        if (ticket) {
            reset({
                status: ticket.status as SupportTicketStatus,
                notes: ticket.notes || null,
            });
        }
    }, [ticket, reset]);

    const updateMutationOptions = useMemo(
        () => ticket ? supportTicketsMutations.update(ticket.id) : supportTicketsMutations.update(''),
        [ticket],
    );

    const { mutate: updateSupportTicket, isPending } = useMutation(updateMutationOptions);

    const handleFormSubmit = useCallback((data: SupportTicketEditFormData) => {
        if (!ticket) return;

        const payload: SupportTicketUpdatePayload = {
            status: data.status,
            notes: data.notes || undefined,
        };

        updateSupportTicket(payload, {
            onSuccess: () => {
                reset();
                onClose();
            },
        });
    }, [ticket, updateSupportTicket, reset, onClose]);

    const handleCancel = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    if (!ticket) {
        return null;
    }

    return (
        <Modal
            title={`Редагувати запит на підтримку #${ticket.ticket_number}`}
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={600}
        >
            <Spin spinning={isPending}>
                <Form
                    layout="vertical"
                    onFinish={handleSubmit(handleFormSubmit)}
                >
                    <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                            <Form.Item
                                label="Статус"
                                required
                                validateStatus={errors.status ? 'error' : ''}
                                help={errors.status?.message}
                            >
                                <Select
                                    {...field}
                                    placeholder="Виберіть статус"
                                    options={statusOptions}
                                    disabled={isPending}
                                    tagRender={(props) => {
                                        const { value } = props;
                                        if (!value) {
                                            return <span />;
                                        }
                                        return (
                                            <StatusTag status={value as SupportTicketStatus} />
                                        );
                                    }}
                                />
                            </Form.Item>
                        )}
                    />

                    <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                            <Form.Item
                                label="Примітки"
                                validateStatus={errors.notes ? 'error' : ''}
                                help={errors.notes?.message}
                            >
                                <Input.TextArea
                                    {...field}
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    placeholder="Введіть примітки"
                                    rows={4}
                                    maxLength={255}
                                    showCount
                                    disabled={isPending}
                                />
                            </Form.Item>
                        )}
                    />

                    <Form.Item>
                        <Flex gap="middle" justify="end">
                            <Button
                                onClick={handleCancel}
                                disabled={isPending}
                            >
                                Скасувати
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isPending}
                                disabled={isPending}
                            >
                                Зберегти
                            </Button>
                        </Flex>
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};
